import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Award, Gift, X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import { formatDate } from '@/utils/formatDate';

interface AwardItem {
  id: string;
  title: string;
  description: string | null;
  status: 'PENDING' | 'CLAIMED' | 'EXPIRED';
  tenantName: string;
  tenantLogo: string | null;
  claimedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export default function AwardsPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [awards, setAwards] = useState<AwardItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [confirmClaim, setConfirmClaim] = useState<AwardItem | null>(null);
  const [claimSuccess, setClaimSuccess] = useState(false);

  const fetchAwards = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.get<{ items: AwardItem[] }>('/scout/awards');
      setAwards(data.items);
    } catch {
      setError(t('awards.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    fetchAwards();
  }, [isAuthenticated, navigate, fetchAwards]);

  // Escape key to close modal
  useEffect(() => {
    if (!confirmClaim) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmClaim(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [confirmClaim]);

  const handleClaim = async (award: AwardItem) => {
    try {
      setClaimingId(award.id);
      setConfirmClaim(null);
      await api.post(`/scout/awards/${award.id}/claim`);
      setClaimSuccess(true);
      setTimeout(() => setClaimSuccess(false), 3000);
      await fetchAwards();
    } catch {
      setError(t('awards.actionError'));
    } finally {
      setClaimingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return { color: 'bg-gold/10 text-gold', label: t('awards.status.pending') };
      case 'CLAIMED':
        return { color: 'bg-forest-wash text-forest', label: t('awards.status.claimed') };
      case 'EXPIRED':
        return { color: 'bg-muted text-muted-foreground', label: t('awards.status.expired') };
      default:
        return { color: 'bg-muted text-muted-foreground', label: status };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-warm-faint"
          aria-label={t('common.goBack')}
        >
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <h1 className="text-section font-semibold text-foreground">{t('awards.title')}</h1>
      </div>

      {claimSuccess && (
        <div
          className="mx-4 mb-3 p-3 rounded-xl bg-forest-wash border border-forest/20 text-forest text-small font-medium text-center animate-fade-in"
          role="status"
        >
          {t('awards.claimSuccess', { defaultValue: 'Award claimed!' })}
        </div>
      )}

      <div className="px-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-muted-foreground text-small">{error}</div>
        ) : awards.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="w-12 h-12 text-steel mx-auto mb-3" />
            <p className="text-muted-foreground text-small">{t('awards.empty')}</p>
          </div>
        ) : (
          awards.map((award) => {
            const badge = getStatusBadge(award.status);
            return (
              <div key={award.id} className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-gold" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground text-small">{award.title}</p>
                    {award.description && (
                      <p className="text-caption text-muted-foreground mt-0.5 line-clamp-2">{award.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-block text-caption px-2 py-0.5 rounded-full font-medium ${badge.color}`}>
                        {badge.label}
                      </span>
                      <span className="text-caption text-muted-foreground">{award.tenantName}</span>
                    </div>
                    {award.expiresAt && award.status === 'PENDING' && (
                      <p className="text-caption text-muted-foreground mt-1">
                        {t('awards.expiresOn')} {formatDate(award.expiresAt)}
                      </p>
                    )}
                  </div>

                  {award.status === 'PENDING' && (
                    <button
                      onClick={() => setConfirmClaim(award)}
                      disabled={claimingId === award.id}
                      className="px-3 py-1.5 bg-primary text-white text-caption font-medium rounded-lg hover:bg-forest disabled:opacity-50 flex-shrink-0"
                    >
                      {claimingId === award.id ? t('awards.claiming') : t('awards.claim')}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Claim confirmation modal */}
      {confirmClaim && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg bg-white rounded-t-2xl p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-section font-bold text-foreground">{t('awards.claim')}</h3>
              <button
                onClick={() => setConfirmClaim(null)}
                className="p-1 rounded-lg hover:bg-warm-faint"
                aria-label={t('common.close')}
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 rounded-xl bg-warm-faint mb-4">
              <p className="font-semibold text-foreground">{confirmClaim.title}</p>
              {confirmClaim.description && (
                <p className="text-small text-muted-foreground mt-1">{confirmClaim.description}</p>
              )}
              <p className="text-caption text-muted-foreground mt-2">{confirmClaim.tenantName}</p>
            </div>
            <p className="text-small text-muted-foreground mb-4">{t('awards.claimConfirm')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmClaim(null)}
                className="flex-1 py-3 rounded-xl border border-warm-border text-foreground font-medium text-small"
              >
                {t('status.cancel')}
              </button>
              <button
                onClick={() => handleClaim(confirmClaim)}
                className="flex-1 py-3 rounded-xl bg-primary text-white font-medium text-small"
              >
                {t('awards.claim')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
